import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import ToggleSwitch from '../../../components/ToggleSwitch/ToggleSwitch';
import axios from 'axios';
import config from '../../../config';
import styles from '../Settings.module.css';

// Dietary preferences are now an array of strings
type DietaryPreferenceType = 'vegan' | 'vegetarian' | 'pescatarian' | 'gluten_free' | 'dairy_free' | 'nut_free' | 'egg_free' | 'soy_free' | 'shellfish_free' | 'fish_free' | 'sesame_free' | 'low_fodmap' | 'keto' | 'paleo' | 'atkins' | 'corn_free' | 'mustard_free';

interface HealthConcern {
  id: number;
  name: string;
  description: string;
  is_active: boolean;
}

interface UserHealthConcern {
  id: string;
  health_concern_id?: string;
  is_active: boolean;
  health_concern: {
    id: string;
    name: string;
    display_name?: string;
    description?: string;
    is_active?: boolean;
  };
  notes?: string;
}

// Dietary hierarchy system
const dietaryHierarchy = {
  vegan: {
    conflicts: ['vegetarian', 'pescatarian', 'dairy_free', 'fish_free', 'shellfish_free', 'egg_free', 'paleo'],
    includes: ['gluten_free', 'nut_free', 'soy_free', 'sesame_free', 'corn_free', 'mustard_free', 'low_fodmap']
  },
  vegetarian: {
    conflicts: ['pescatarian', 'fish_free', 'shellfish_free'],
    includes: ['dairy_free', 'egg_free', 'gluten_free', 'nut_free', 'soy_free', 'sesame_free', 'corn_free', 'mustard_free', 'low_fodmap']
  },
  pescatarian: {
    conflicts: ['fish_free', 'shellfish_free'],
    includes: ['dairy_free', 'egg_free', 'gluten_free', 'nut_free', 'soy_free', 'sesame_free', 'corn_free', 'mustard_free', 'low_fodmap']
  }
};

const dietaryOptions = [
  { key: 'vegan', label: 'Vegan' },
  { key: 'vegetarian', label: 'Vegetarian' },
  { key: 'pescatarian', label: 'Pescatarian' },
  { key: 'gluten_free', label: 'Gluten-Free' },
  { key: 'dairy_free', label: 'Dairy-Free' },
  { key: 'nut_free', label: 'Nut-Free' },
  { key: 'egg_free', label: 'Egg-Free' },
  { key: 'soy_free', label: 'Soy-Free' },
  { key: 'shellfish_free', label: 'Shellfish-Free' },
  { key: 'fish_free', label: 'Fish-Free' },
  { key: 'sesame_free', label: 'Sesame-Free' },
  { key: 'low_fodmap', label: 'Low FODMAP' },
  { key: 'keto', label: 'Keto' },
  { key: 'paleo', label: 'Paleo' },
  { key: 'atkins', label: 'Atkins' },
  { key: 'corn_free', label: 'Corn-Free' },
  { key: 'mustard_free', label: 'Mustard-Free' }
];

interface DietaryTabProps {
  onNotification: (type: 'success' | 'error', message: string) => void;
}

const DietaryTab: React.FC<DietaryTabProps> = ({ onNotification }) => {
  const { user, saveUserPreferencesToStorage } = useAuth();

  // Format health concern names for display (remove underscores, capitalize)
  const formatHealthConcernName = (name: string): string => {
    return name
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };
  const [availableHealthConcerns, setAvailableHealthConcerns] = useState<HealthConcern[]>([]);
  const [isLoadingHealthConcerns, setIsLoadingHealthConcerns] = useState(false);
  
  // Use user object directly - no local state needed
  const preferences = user?.preferences?.dietary || [];
  const userHealthConcerns = user?.preferences?.healthConcerns || [];

  // Check if an option should be disabled due to conflicts
  const isOptionDisabled = (optionKey: string): boolean => {
    for (const selectedPreference of preferences) {
      if (dietaryHierarchy[selectedPreference as keyof typeof dietaryHierarchy]?.conflicts?.includes(optionKey)) {
        return true;
      }
    }
    return false;
  };

  // Get the relationship status for an option
  const getOptionStatus = (optionKey: string): 'conflict' | 'neutral' => {
    for (const selectedPreference of preferences) {
      if (dietaryHierarchy[selectedPreference as keyof typeof dietaryHierarchy]?.conflicts?.includes(optionKey)) {
        return 'conflict';
      }
    }
    return 'neutral';
  };

  // Load available health concerns
  useEffect(() => {
    const loadAvailableHealthConcerns = async () => {
      if (!user) return;
      
      setIsLoadingHealthConcerns(true);
      try {
        const response = await axios.get(`${config.API_BASE_URL}/user/health-concerns/available`, {
          headers: {
            'Authorization': `Bearer ${user.accessToken}`,
            'Content-Type': 'application/json'
          }
        });
        setAvailableHealthConcerns(response.data);
      } catch (error) {
        console.error('Error loading health concerns:', error);
        onNotification('error', 'Failed to load health concerns');
      } finally {
        setIsLoadingHealthConcerns(false);
      }
    };

    loadAvailableHealthConcerns();
  }, [user, onNotification]);

  const handlePreferenceChange = async (key: DietaryPreferenceType) => {
    const isCurrentlySelected = preferences.includes(key);
    const currentCount = preferences.length;
    
    // If trying to select and already at max (3), don't allow
    if (!isCurrentlySelected && currentCount >= 3) {
      return;
    }
    
    // If trying to select a conflicting option, don't allow
    if (!isCurrentlySelected && isOptionDisabled(key)) {
      return;
    }
    
    // Create new preferences array
    let newPreferences = isCurrentlySelected
      ? preferences.filter((pref: string) => pref !== key)
      : [...preferences, key];
    
    // If selecting a parent diet, remove all conflicting children
    if (!isCurrentlySelected && dietaryHierarchy[key as keyof typeof dietaryHierarchy]) {
      const conflicts = dietaryHierarchy[key as keyof typeof dietaryHierarchy]?.conflicts || [];
      newPreferences = newPreferences.filter((pref: string) => !conflicts.includes(pref));
    }
    
    // Save to localStorage for persistence
    saveUserPreferencesToStorage({
      dietary: newPreferences,
      healthConcerns: user?.preferences?.healthConcerns || [],
      ingredientPreferences: user?.preferences?.ingredientPreferences || []
    });

    // Save to backend immediately
    try {
      const response = await axios.post(`${config.API_BASE_URL}/user/my-preferences-array`, {
        dietary: newPreferences
      }, {
        headers: {
          'Authorization': `Bearer ${user?.accessToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      onNotification('success', 'Dietary preferences updated!');
    } catch (error) {
      console.error('Error saving dietary preferences:', error);
      onNotification('error', 'Failed to save dietary preferences');
    }
  };

  const handleHealthConcernToggle = async (healthConcernId: number) => {
    if (!user) return;

    const existingConcern = userHealthConcerns.find((uc: UserHealthConcern) => {
      // Handle both data formats
      if (uc.health_concern_id) {
        // Format 1: has health_concern_id field
        return uc.health_concern_id === healthConcernId.toString();
      } else if (uc.health_concern && uc.health_concern.id) {
        // Format 2: health concern data directly in health_concern object
        return uc.health_concern.id === healthConcernId.toString();
      }
      return false;
    });
    
    const isCurrentlySelected = !!existingConcern;
    const selectedCount = userHealthConcerns.length;
    
    // If trying to select and already at max (3), don't allow
    if (!isCurrentlySelected && selectedCount >= 3) {
      return;
    }

    // Update local state immediately for better UX
    let updated: UserHealthConcern[];
    let tempId: string | null = null;
    
    if (existingConcern) {
      // Remove existing concern
      updated = userHealthConcerns.filter((uc: UserHealthConcern) => uc.id !== existingConcern.id);
    } else {
      // Create new concern with temporary ID
      tempId = Date.now().toString();
      const newConcern: UserHealthConcern = {
        id: tempId,
        health_concern_id: healthConcernId.toString(),
        is_active: true,
        health_concern: {
          id: healthConcernId.toString(),
          name: availableHealthConcerns.find(hc => hc.id === healthConcernId)?.name || ''
        }
      };
      updated = [...userHealthConcerns, newConcern];
    }

    // Update local state immediately
    saveUserPreferencesToStorage({
      dietary: user?.preferences?.dietary || [],
      healthConcerns: updated,
      ingredientPreferences: user?.preferences?.ingredientPreferences || []
    });

    try {
      if (existingConcern) {
        // Remove health concern
        await axios.delete(`${config.API_BASE_URL}/user/health-concerns/${existingConcern.id}`, {
          headers: {
            'Authorization': `Bearer ${user.accessToken}`,
            'Content-Type': 'application/json'
          }
        });
        onNotification('success', 'Health concern removed');
      } else {
        // Add health concern
        const response = await axios.post(`${config.API_BASE_URL}/user/health-concerns`, {
          health_concern_id: healthConcernId
        }, {
          headers: {
            'Authorization': `Bearer ${user.accessToken}`,
            'Content-Type': 'application/json'
          }
        });
        
        // Update with real ID from response
        const realId = response.data.id;
        const finalUpdated = updated.map(uc => 
          uc.id === tempId ? { ...uc, id: realId } : uc
        );
        
        saveUserPreferencesToStorage({
          dietary: user?.preferences?.dietary || [],
          healthConcerns: finalUpdated,
          ingredientPreferences: user?.preferences?.ingredientPreferences || []
        });
        
        onNotification('success', 'Health concern added');
      }
    } catch (error) {
      console.error('Error updating health concern:', error);
      onNotification('error', 'Failed to update health concern');
      
      // Revert on error
      saveUserPreferencesToStorage({
        dietary: user?.preferences?.dietary || [],
        healthConcerns: userHealthConcerns,
        ingredientPreferences: user?.preferences?.ingredientPreferences || []
      });
    }
  };

  return (
    <div className={styles.tabContent}>
      <div className={styles.sectionHeader}>
        <h3>Dietary Preferences</h3>
        <p className={styles.sectionDescription}>
          Select up to 3 dietary preferences that best describe your eating habits. 
          This helps us provide personalized recipe recommendations.
        </p>
        <p className={styles.selectionCounter}>
          {preferences.length}/3 selected
        </p>
      </div>
      
      <div className={styles.preferencesGrid}>
        {dietaryOptions.map(({ key, label }) => {
          const isChecked = preferences.includes(key as DietaryPreferenceType);
          const selectedCount = preferences.length;
          const isAtMax = selectedCount >= 3;
          const isDisabled = (isAtMax && !isChecked) || isOptionDisabled(key);
          const status = getOptionStatus(key);

          return (
            <ToggleSwitch
              key={key}
              id={key}
              checked={isChecked}
              onChange={() => handlePreferenceChange(key as DietaryPreferenceType)}
              disabled={isDisabled}
              label={label}
              statusIndicator={status === 'conflict' ? {
                text: 'Vegan conflict',
                type: 'conflict'
              } : undefined}
            />
          );
        })}
      </div>

      {/* Health Concerns Section */}
      <div className={styles.sectionHeader} style={{ marginTop: '2rem' }}>
        <h3>Health Concerns</h3>
        <p className={styles.sectionDescription}>
          Select up to 3 health conditions that affect your dietary choices.
        </p>
        <p className={styles.selectionCounter}>
          {userHealthConcerns.length}/3 selected
        </p>
      </div>
      
      <div className={styles.preferencesGrid}>
        {isLoadingHealthConcerns ? (
          <p>Loading health concerns...</p>
        ) : (
          availableHealthConcerns.map((concern) => {
            const isChecked = userHealthConcerns.some((uc: UserHealthConcern) => {
              if (uc.health_concern_id) {
                return uc.health_concern_id === concern.id.toString();
              } else if (uc.health_concern && uc.health_concern.id) {
                return uc.health_concern.id === concern.id.toString();
              }
              return false;
            });
            const selectedCount = userHealthConcerns.length;
            const isAtMax = selectedCount >= 3;
            const isDisabled = isAtMax && !isChecked;

            return (
              <ToggleSwitch
                key={concern.id}
                id={`health-${concern.id}`}
                checked={isChecked}
                onChange={() => handleHealthConcernToggle(concern.id)}
                disabled={isDisabled}
                label={formatHealthConcernName(concern.name)}
              />
            );
          })
        )}
      </div>

    </div>
  );
};

export default DietaryTab;
